import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactPlayer from 'react-player/lazy';

import { makeStyles } from 'src/makeStyles';
import { useTracking } from 'react-tracking';
import { trackMediaPlayer } from '../../utils/tracking';
import { cueTvMediaUrlRegex, vimeoMediaUrlRegex, facebookMediaUrlRegex } from '../../constants/consts';
import VimeoOTTPlayer from '../../utils/vimeoOttPlayer';

ReactPlayer.addCustomPlayer(VimeoOTTPlayer);

const useStyles = makeStyles()((theme, props) => ({
  root: {
    position: 'relative',
  },
  rootPopup: {
    position: 'relative',
    width: '100%',
    justifyContent: 'center',
    display: 'flex',
    background: 'transparent',
  },
  playerWrapper: {
    width: props?.isFacebookUrl ? 'auto' : '100%',
    height: '100%',
  },
  reactPlayer: {
    top: 0,
    left: 0,
  },
  videoWrapper: {
    '& div': {
      '& div': {
        padding: '0 !important',
        height: '100%',
        width: '100%',
      },
    },
  },
  playerWrapperThumb: {
    width: 168,
    minWidth: 168,
    height: 95,
    minHeight: 95,
  },
  hidePlayButton: {
    '& .react-player__play-icon': {
      display: 'none',
    },
    '& .react-player__shadow': {
      display: 'none !important',
    },
  },
  showPlayButton: {
    '& .react-player__play-icon': {
      display: 'flex',
      borderWidth: '10px 0px 10px 18px !important',
    },
    '& .react-player__shadow': {
      display: 'flex !important',
    },
  },
  limitSize: {
    maxWidth: '95vw',
    maxHeight: '95vh',
  },
  brokenVideo: {
    position: 'absolute',
    top: '50%',
    marginTop: '-12px',
    left: '50%',
    marginLeft: '-12px',
  },
}));

const MediaPlayer = ({
  record,
  type,
  onThumbClick,
  showPlayButton,
  showControls,
  className,
  selfSize,
  popUp,
  customWidth,
  customHeight,
  autoplay,
  muted,
  placeholder,
  banner,
  videoClass,
}) => {
  const videoRef = useRef(null);
  const isThumb = type === 'thumb';
  const thumbnail = popUp ? null : record?.thumbnail?.urls?.original;
  const videoUrl = record?.url || record?.file?.urls?.original;
  const isFacebookUrl = videoUrl?.match(facebookMediaUrlRegex);
  const { classes, cx } = useStyles({ isFacebookUrl });

  const tracking = useTracking();
  const handleStartVideo = () => {
    if (record) {
      const { description, title } = record;
      tracking.trackEvent(trackMediaPlayer({ videoUrl, description, title }));
    }
  };
  const [play, setPlayPause] = useState(autoplay);

  const ready = () => {
    if (banner) setPlayPause(true);
  };

  return (
    <div
      className={cx(
        { [classes.root]: !popUp },
        { [classes.rootPopup]: popUp },
        { [classes.limitSize]: selfSize },
        { [classes.playerWrapper]: !selfSize },
        className,
        {
          [classes.playerWrapperThumb]: isThumb,
          [classes.hidePlayButton]: isThumb,
          [classes.showPlayButton]: showPlayButton,
        },
      )}
      ref={videoRef}
    >
      {videoUrl?.match(cueTvMediaUrlRegex) || ReactPlayer.canPlay(videoUrl) ? (
        <>
          <ReactPlayer
            className={cx(
              classes.reactPlayer,
              { [classes.thumbClass]: isThumb },
              { [classes.videoWrapper]: !!videoUrl?.match(vimeoMediaUrlRegex) },
              videoClass,
            )}
            url={videoUrl}
            playing={play}
            muted={muted}
            controls={showControls && !isThumb}
            onClick={() => onThumbClick(record)}
            light={isThumb || thumbnail ? thumbnail : false} // show thumbnail only if it is exist
            {...(selfSize ? {} : { width: isFacebookUrl ? '200' : customWidth, height: customHeight })}
            onStart={handleStartVideo}
            onReady={ready}
            fallback={<p>Loading....</p>}
          />
        </>
      ) : (
        <>{placeholder}</>
      )}
    </div>
  );
};

MediaPlayer.propTypes = {
  record: PropTypes.object,
  type: PropTypes.oneOf(['full', 'thumb']),
  onThumbClick: PropTypes.func,
  showPlayButton: PropTypes.bool,
  showControls: PropTypes.bool,
  selfSize: PropTypes.bool,
  popUp: PropTypes.bool,
  customWidth: PropTypes.string,
  customHeight: PropTypes.string,
  autoplay: PropTypes.bool,
  muted: PropTypes.bool,
};

MediaPlayer.defaultProps = {
  type: 'full',
  onThumbClick: () => {},
  customHeight: '100%',
  customWidth: '100%',
  autoplay: false,
  muted: false,
};

export default MediaPlayer;
